/* eslint-disable react/destructuring-assignment */
/* eslint-disable react/prop-types */
/* eslint-disable max-len */
/* eslint-disable no-nested-ternary */
import React, {
  useState, useMemo,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from '@emotion/styled';
import moment from 'moment-timezone';
import PropTypes from 'prop-types';
import IcomoonReact from 'icomoon-react';

import Select, { components } from 'react-select';
import { useTheme } from 'emotion-theming';
import Shimmer from '../../../../shared/_TBComponents/Shimmer';
import iconSet from '../../../../shared/images/teambuildr-selection.json';

import {
  saveCalendarExercise,
  saveNonDateBasedWorkoutExercise,
  setExpandedItem,
  focusNextPreSaveItem,
  focusNextPreSaveProgramItem,
} from '../../ducks/calendarActions';
import usePreSaveAutoSave, { hasAutoSavedPreSaveItem } from '../../hooks/usePreSaveAutoSave';

const Container = styled('div')`
  display: flex;
  width: 100%;
  justify-content: space-between;
`;

const ShimmerInput = styled('input')`
  height: 20px;
  width: 100%;
  border: none;
  border-radius: 5px;
`;

const IconContainer = styled('div')`
  height: 24px;
  width: 24px;
  border-radius: ${(props) => (props.square ? '4px' : '50%')};
  color: ${(props) => (props.color ? props.color : props.theme.colors.text)};
  border: ${(props) => (props.square ? 'none' : 'solid 1px')};
  display: flex;
  justify-content: center;
  align-items: center;
  margin-right: ${(props) => (props.square ? '8px' : '5px')};
  ${(props) => props.backgroundColor && `background: ${props.backgroundColor}`};
  ${(props) => props.square && 'padding: 4px'}; 
`;

const PreSaveProgressionTable = ({
  contentIndex,
  content,
  date,
  dayNumber,
}) => {
  /** Whether THIS mount is the one that auto-saves — a fresh pre-save item that
   * hasn't already been through auto-save. Captured in a lazy initializer so it
   * is known during the first render. No draft check here, unlike the exercise
   * tables: this one keeps no preSaveTableLocalState, so there is no
   * in-progress edit to protect. */
  const [willAutoSave] = useState(() => content.preSave === true
    && !hasAutoSavedPreSaveItem(content.assignedId));

  /** Seeded from willAutoSave so the shimmer is the first thing painted rather
   * than the second — deciding this in an effect would flash the dropdown for a
   * frame before replacing it. */
  const [isAdding, setIsAdding] = useState(willAutoSave);
  /** isAdding is per-mount, so a remount mid-save (CellMeasurer, scroll)
   * would bring the step picker back while the save is still in flight - a
   * step picked then hits the thunk's re-entry guard and is dropped. The
   * Redux flag survives the remount. */
  const isSavingThisItem = useSelector(
    (state) => !!state.calendar.ui.savingAssignedIds[content.assignedId],
  );
  const isSaving = isAdding || isSavingThisItem;

  const currentUser = useSelector((state) => state.auth.data.currentUser);
  const selectedDate = useSelector((state) => state.calendar.data.selectedDate);
  const effectiveDate = date || selectedDate;
  const programType = useSelector((state) => state.calendar.data.programType);

  const [selectedProgressionValue, setSelectedProgressionValue] = useState(null);

  const dispatch = useDispatch();
  const { colors } = useTheme();

  const errorAddReset = () => {
    setIsAdding(false);
  };

  /** Land the newly saved item expanded, matching the other pre-save tables.
   * SAVE_CALENDAR_EXERCISE_FULFILLED defaults every new save to collapsed. */
  const expandSavedItem = (savedItem) => {
    if (savedItem?.assignedId == null) return;
    dispatch(setExpandedItem(savedItem));
  };

  const extractSecondNumber = (str) => {
    const match = str.match(/^\d+-(\d+)$/);
    return match ? parseInt(match[1], 10) : null;
  };

  const handleSaveProgression = (
    dbSelectedProgression,
  ) => {
    /** Called programmatically by the mount auto-save as well as by the
     * dropdown, so it can arrive with nothing selected — a progression with no
     * levels configured yields no steps. Hand the dropdown back rather than
     * leaving the shimmer up with no save behind it. */
    if (!dbSelectedProgression?.id) {
      setIsAdding(false);
      return;
    }
    setIsAdding(true);

    const submissionItem = {
      assignedId: extractSecondNumber(content.assignedId),
      exerciseId: content.exerciseId,
      /** Conditional key based on programType: "date" OR "dayNumber" */
      [programType === 'free-form-program' ? 'dayNumber' : 'date']:
        programType === 'free-form-program'
          ? dayNumber
          : moment(effectiveDate).format('YYYY-MM-DD'),
      order: contentIndex,
      description: content?.exercise?.description || '',
      type: 'progression',
      step: dbSelectedProgression.id,
    };
    if (content.groupingLetter) {
      submissionItem.groupingLetter = content.groupingLetter;
    }
    if (programType === 'free-form-program') {
      dispatch(saveNonDateBasedWorkoutExercise(
        currentUser,
        contentIndex,
        submissionItem,
        dayNumber,
        errorAddReset,
        {
          onSuccess: (savedItem) => {
            expandSavedItem(savedItem);
            dispatch(focusNextPreSaveProgramItem(contentIndex, dayNumber));
          },
          exerciseName: content.title,
        },
        content.assignedId,
      ));
    } else {
      dispatch(saveCalendarExercise(
        currentUser,
        contentIndex,
        submissionItem,
        moment(effectiveDate).format('YYYY-MM-DD'),
        errorAddReset,
        {
          onSuccess: (savedItem) => {
            expandSavedItem(savedItem);
            dispatch(focusNextPreSaveItem(contentIndex, effectiveDate));
          },
          exerciseName: content.title,
        },
        content.assignedId,
      ));
    }
  };

  /** Derived rather than held in state and filled by an effect: the auto-save
   * below needs the first step during its own mount effect, and effect-backed
   * state is still empty at that point. Deriving once also means the dropdown
   * and the auto-save can't disagree about what the first option is. */
  const progressionSteps = useMemo(() => {
    /** `levels` is what the API returns for a progression's step count
     * (exerciseDetail.levels = exer_detail when type is 'P'), and a pre-save
     * item is the sidebar exercise spread flat, so it carries that field.
     * `maxProgressionSteps` is a different column the API leaves null here —
     * reading it alone is why this list came up empty. Kept as a fallback for
     * an item built from a saved-item shape. */
    const stepCount = parseInt(content?.levels ?? content?.maxProgressionSteps, 10);
    if (!stepCount || Number.isNaN(stepCount)) return [];
    return Array.from({ length: stepCount }, (_, i) => ({
      id: i + 1,
      name: `Step ${i + 1}`,
      includeCheckmark: selectedProgressionValue?.id === i + 1,
      checkmarkColor: colors.textTertiary,
    }));
  }, [content, selectedProgressionValue, colors]);

  /** Assign a freshly added progression immediately at its first step, instead
   * of parking it behind the dropdown. */
  usePreSaveAutoSave({
    assignedId: content.assignedId,
    enabled: willAutoSave,
    onAutoSave: () => handleSaveProgression(progressionSteps[0]),
  });

  const customStyles = {
    control: (provided, state) => ({
      ...provided,
      borderTopRightRadius: state.options.some((option) => option.id === 'sets') && '0px',
      borderBottomRightRadius: state.options.some((option) => option.id === 'sets') && '0px',
      fontFamily: 'Nunito Sans',
      fontSize: '16px',
      fontWeight: 400,
      background: colors.surfaceInput,
      border: `1px solid ${colors.borderLight}`,
      height: '38px',
      minHeight: '38px',
      borderColor: state.isFocused ? colors.hover : colors.borderLight,
      boxShadow: state.isFocused ? 'none' : 'none',
      '&:hover': {
        borderColor: state.isFocused ? colors.hover : colors.borderLight,
      },
    }),
    container: (provided) => ({
      ...provided,
      width: '100%',
    }),
    option: (provided, state) => ({
      ...provided,
      paddingLeft: state.data.parentId ? 35 : 15,
      padding: '4px',
      backgroundColor: colors.surfaceElevated,
      color: state.isSelected ? colors.textTertiary : colors.text,
      fontFamily: 'Nunito Sans',
      fontSize: '16px',
      marginBottom: '8px',
      fontWeight: 400,
      '&:hover': {
        backgroundColor: colors.surfaceSecondary,
      },
    }),
    multiValue: (styles) => ({
      ...styles,
      backgroundColor: colors.surfaceSecondary,
      border: `1px solid ${colors.borderLight}`,
      borderRadius: '4px',
    }),
    multiValueLabel: (styles) => ({
      ...styles,
      color: colors.text,
    }),
    multiValueRemove: (styles) => ({
      ...styles,
      color: colors.textSecondary,
    }),
    menuPortal: (base) => ({
      // Menu is portaled to document.body; lift it above MonthEditPopover
      // (z-index 50) so it doesn't render underneath. Matches the exercise
      // picker convention in this popover.
      ...base,
      zIndex: 99999,
    }),
    menu: (provided) => ({
      ...provided,
      backgroundColor: colors.surfaceElevated,
      color: colors.text,
      border: '0px',
      minWidth: '250px',
      padding: '16px',
      marginTop: '0px',
      marginBottom: '0px',
      boxShadow: '0px 4px 8px 0px #3031331A',
    }),
    menuList: (provided) => ({
      ...provided,
      maxHeight: '500px',
    }),
    group: (provided) => ({
      ...provided,
      padding: '0px',
    }),
    groupHeading: (provided) => ({
      ...provided,
      fontSize: '16px',
      fontWeight: 700,
      color: colors.textSecondary,
      paddingLeft: '0px',
      paddingRight: '0px',
      marginBottom: '8px',
      fontFamily: 'Nunito Sans',
      textTransform: 'none',
    }),
    singleValue: (provided) => ({
      ...provided,
      color: colors.text,
    }),
    placeholder: (provided) => ({
      ...provided,
      color: colors.textSecondary,
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    }),
    input: (provided, state) => ({
      ...provided,
      color: state.isDisabled ? 'inherit' : provided.color, /** Use 'inherit' to maintain original color */
    }),
    indicatorSeparator: (base) => ({
      ...base,
      display: 'none',
    }),
  };

  const setSelectedProgressionTrigger = (option) => {
    handleSaveProgression(option);
  };

  const CustomOption = (props) => (
    <components.Option {...props}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: props.data.includeCheckmark ? 'space-between' : '' }}>
        <div style={{ display: 'flex' }}>
          {!props.data.avatar ? (
            <>
              {props?.data?.includeIconContainer ? (
                <IconContainer color={props.data.iconContainerColor} square={props.data?.squareContainer} backgroundColor={props.data?.backgroundColor}>
                  <IcomoonReact
                    iconSet={iconSet}
                    size={16}
                    icon={props.data.icon}
                    color={props.data.color || colors.text}
                  />
                </IconContainer>
              ) : (
                <IcomoonReact
                  iconSet={iconSet}
                  size={16}
                  icon={props.data.icon}
                  color={props.data.color || colors.text}
                  style={{ marginRight: 5 }}
                />
              )}
            </>
          ) : (
            <div style={{
              marginRight: 5,
            }}
            >
              {props.data.avatar}
            </div>
          )}
          {props.children}
        </div>
        {props.data.includeCheckmark && (
        <IcomoonReact
          iconSet={iconSet}
          size={12}
          icon='checkmark'
          color={props.data.checkmarkColor || colors.text}
          style={{ marginRight: 5 }}
        />
        )}
      </div>
    </components.Option>
  );

  return (
    <Container>
      {!isSaving ? (
        <Select
          getOptionLabel={(option) => option.name}
          getOptionValue={(option) => option.id}
          options={progressionSteps}
          components={{
            Option: CustomOption,
          }}
          onChange={(option) => {
            setSelectedProgressionTrigger(option);
            setSelectedProgressionValue(option);
          }}
          isSearchable
          styles={customStyles}
          classNamePrefix='progression-table-picker'
          menuPortalTarget={document.body}
          menuPosition='fixed'
          menuShouldBlockScroll
          placeholder='Select step the athlete will be shown that corresponds to their level'
        />
      ) : (
        <Shimmer>
          <ShimmerInput />
        </Shimmer>
      )}
    </Container>
  );
};

PreSaveProgressionTable.propTypes = {
  contentIndex: PropTypes.number.isRequired,
  content: PropTypes.instanceOf(Object).isRequired,
  date: PropTypes.string,
  dayNumber: PropTypes.string,
};

PreSaveProgressionTable.defaultProps = {
  date: null,
  dayNumber: null,
};

export default PreSaveProgressionTable;
